// src/encounter/lib/step-pass.ts
//
// 16 Oct, Antonin: "give a choice to the practitioner to pass or ignore something". A pass is the
// practitioner saying "Nothing to record" for a block. DISPLAY ONLY: it changes no sign gate, no autosave and nothing in the note.
// Stored per encounter in this browser's localStorage (every read and write guarded: storage can be missing or blocked, and the
// page then simply has no passes). A pass counts only while its step holds no answer (`effectivePasses`), so an answer entered
// afterwards ends it without any further action. Persisting a pass beyond this browser needs a field on the note: a gap for Andrew.
import { createContext, useCallback, useContext, useState } from 'react';
import type { PaStepKey } from './encounter-steps';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** The blocks that can be passed: the ones the ✓ rules read. Wrap-up is never passed. Documents only while `docsBlockV2` is OFF
 *  (PROD-3397, Antonin 3 Nov: "remove 'nothing to record'"): use `isPassableStep`, which reads the switch. */
export const PASSABLE_STEPS: readonly PaStepKey[] = ['hpi', 'risk', 'ros', 'pe', 'docs', 'ap'];

export function isPassableStep(step: PaStepKey): boolean {
  if (step === 'docs' && isEncounterFlagOn('docsBlockV2')) return false;
  return PASSABLE_STEPS.includes(step);
}

/** Every pass key starts with this (the session cleanup removes them all when a session ends). */
export const STEP_PASS_KEY_PREFIX = 'scrips.stepPasses.';
const keyFor = (encounterId: string) => `${STEP_PASS_KEY_PREFIX}${encounterId}`;

export function readPasses(encounterId: string | null | undefined): PaStepKey[] {
  if (!encounterId) return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(keyFor(encounterId)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is PaStepKey => PASSABLE_STEPS.includes(v as PaStepKey)) : [];
  } catch {
    return [];
  }
}

function writePasses(encounterId: string, steps: readonly PaStepKey[]): void {
  try {
    window.localStorage.setItem(keyFor(encounterId), JSON.stringify(steps));
  } catch {
    // Storage unavailable: the pass lasts for this page's life only.
  }
}

/** The passes that count: passed and still holding no answer. */
export function effectivePasses(stored: ReadonlySet<PaStepKey>, answered: ReadonlySet<PaStepKey>): Set<PaStepKey> {
  return new Set([...stored].filter((s) => !answered.has(s)));
}

/** The page's stored passes for this encounter. `enabled` false → none, and nothing is read or written. */
export function useStepPasses(encounterId: string | null | undefined, enabled: boolean) {
  const [state, setState] = useState<{ id: string | null | undefined; steps: PaStepKey[] }>(() => ({
    id: encounterId,
    steps: enabled ? readPasses(encounterId) : [],
  }));
  // The encounter changed under the page: read that encounter's passes (adjusted during render, never an effect).
  let steps = state.steps;
  if (state.id !== encounterId) {
    steps = enabled ? readPasses(encounterId) : [];
    setState({ id: encounterId, steps });
  }
  const commit = useCallback(
    (next: PaStepKey[]) => {
      setState({ id: encounterId, steps: next });
      if (encounterId) writePasses(encounterId, next);
    },
    [encounterId],
  );
  const toggle = useCallback(
    (step: PaStepKey) => commit(steps.includes(step) ? steps.filter((s) => s !== step) : [...steps, step]),
    [commit, steps],
  );
  const drop = useCallback((dropped: readonly PaStepKey[]) => commit(steps.filter((s) => !dropped.includes(s))), [commit, steps]);
  return { stored: enabled ? new Set(steps) : new Set<PaStepKey>(), toggle, drop };
}

/** What the long page's blocks and ribbon read: the passes that count and the toggle. Absent → no pass UI at all. */
export interface StepPass {
  passed: ReadonlySet<PaStepKey>;
  onToggle: (step: PaStepKey) => void;
}
export const StepPassContext = createContext<StepPass | undefined>(undefined);
export function useStepPass(): StepPass | undefined {
  return useContext(StepPassContext);
}
